import apiProxy from '@/app/features/shared/utils/apiProxy';
import { AppDispatch, RootState } from '@/app/store';
import { setUser, setError, setProfilePictureUrl } from '@/app/store/slices/userSlice';
import { SysUser_OutputPayload } from '@/app/types/core/identitiesTypes';
import * as coreConstants from '@/app/constants/coreConstants';
import * as apiConstants from '@/app/constants/apiConstants';
import { logger } from '@/app/utils/logger';
import {
    uploadProfilePicture as apiUploadProfilePicture,
    selectDefaultAvatar as apiSelectDefaultAvatar,
    deleteProfilePicture as apiDeleteProfilePicture,
} from '@/app/services/api/profilePictureService';

export const fetchUserThunk = (loginName: string) => async (dispatch: AppDispatch) => {
    try {
        const params = new URLSearchParams();
        params.append('partitionKey', `${coreConstants.PARTITION_KEY}`);
        params.append('loginName', `${loginName}`);
        const queryString = params.toString();
        const url = `${apiConstants.API_BASE_URL}${apiConstants.API_USERS_BY_LOGIN_NAME}?${queryString}`;
        const response = await apiProxy({
            method: 'GET',
            url: url,
        });
        const result = response.data;
        if (result.succeeded && result.data) {
            dispatch(setUser({ user: result.data.user, error: '' }));
        }
        else {
            const error = `Failed to fetch user: ${result.messages.join(', ')}`;
            dispatch(setError(error));
        }
    } catch (error) {
        logger.error('Failed to fetch user', error, { loginName });
    }
};

export const uploadProfilePictureThunk = (file: File, loginName: string) => async (dispatch: AppDispatch) => {
    try {
        const url = await apiUploadProfilePicture(file);
        dispatch(setProfilePictureUrl(url));
        await dispatch(fetchUserThunk(loginName) as any);
        return url;
    } catch (error) {
        logger.error('Failed to upload profile picture', error);
        throw error;
    }
};

export const selectDefaultAvatarThunk = (defaultAvatarId: number, loginName: string) => async (dispatch: AppDispatch) => {
    try {
        const url = await apiSelectDefaultAvatar(defaultAvatarId);
        dispatch(setProfilePictureUrl(url));
        await dispatch(fetchUserThunk(loginName) as any);
        return url;
    } catch (error) {
        logger.error('Failed to select default avatar', error);
        throw error;
    }
};

export const deleteProfilePictureThunk = (loginName: string) => async (dispatch: AppDispatch) => {
    try {
        await apiDeleteProfilePicture();
        dispatch(setProfilePictureUrl(null));
        await dispatch(fetchUserThunk(loginName) as any);
    } catch (error) {
        logger.error('Failed to delete profile picture', error);
        throw error;
    }
};

export const fetchMyInfoThunk = () => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
        const url = `${apiConstants.API_BASE_URL}${apiConstants.API_USERS_ME}`;
        const response = await apiProxy({
            method: 'GET',
            url: url,
        });
        const result = response.data;
        if (result.succeeded && result.data) {
            // The get-my_info endpoint returns token content, we need to map it to user format.
            // Token content does not include profilePictureUrl, so preserve the existing value from Redux state.
            const tokenContent = result.data.tokenContent;
            const existingProfilePictureUrl = getState().user.user?.profilePictureUrl ?? null;
            const user: SysUser_OutputPayload = {
                id: tokenContent.id || 0,
                guid: tokenContent.guid || null,
                isDeleted: false,
                status: 'Active',
                createdAt: null,
                createdBy: null,
                modifiedAt: null,
                modifiedBy: null,
                deletedAt: null,
                deletedBy: null,
                partitionKey: null,
                type: null,
                userName: tokenContent.userNameIdentifier,
                displayName: tokenContent.name,
                givenName: tokenContent.givenName,
                surname: tokenContent.surname,
                email: tokenContent.email,
                profilePictureUrl: existingProfilePictureUrl,
                logins: [],
                roles: tokenContent.roles || []
            };
            dispatch(setUser({ user: user, error: '' }));
        }
        else {
            const error = `Failed to fetch my info: ${result.messages.join(', ')}`;
            dispatch(setError(error));
        }
    } catch (error) {
        logger.error('Failed to fetch my info', error);
        dispatch(setError(`Failed to fetch my info: ${error}`));
    }
};  